import './dangerButton.scss';

import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  RadioGroup,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { InspectionValues } from '@parallel-fluidics/shared/constants';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import quarantineLot from '../api/quarantineLot';
import useLotTrace from '../hooks/useLotTrace';
import planLotQuarantine from '../utils/planLotQuarantine';

/**
 * Quarantine confirm dialog, shared by the lot detail page and the
 * trace page. Walks the downstream graph for a preview of exactly what
 * will happen — lots quarantined, completed travelers rejected,
 * in-progress travelers flagged, shipped travelers surfaced for
 * customer follow-up — with a scope choice defaulting to the full
 * downstream recall. "This lot only" is the escape hatch for damage
 * that happened after earlier consumption.
 */
export default function QuarantineLotModal({ lot, onClose, onSuccess = () => {} }) {
  const { user } = useUserData();
  const queryClient = useQueryClient();
  const { data: trace, isLoading, error } = useLotTrace(lot.id);
  const [scope, setScope] = useState('downstream');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const plan = useMemo(() => (trace ? planLotQuarantine(trace) : null), [trace]);
  const travelerCount = plan
    ? plan.rejected.length + plan.flagged.length + plan.shipped.length : 0;
  const hasDownstream = plan && (Object.keys(trace.lotsById).length > 1 || travelerCount > 0);
  // Disable only when the graph holds nothing to act on at all. With
  // lots already quarantined but travelers still in the graph, the
  // action stays runnable — the api's writes are idempotent, so a
  // re-run heals a partial failure without duplicating notes.
  const nothingToDo = plan && plan.lots.length === 0 && travelerCount === 0;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setReasonError('Reason is required');
      return;
    }
    setReasonError('');
    setSubmitError('');
    setIsSubmitting(true);
    try {
      // Pass the selected scope as-is (it defaults to 'downstream', and
      // the radio isn't rendered when the preview is empty, so it can't
      // have changed) — the api re-walks the graph fresh, so a child
      // created after an empty preview is still caught at execute time.
      const executed = await quarantineLot({
        rootLotId: lot.id,
        scope,
        reason: reason.trim(),
        user: { uid: user.id, email: user.email },
      });
      const parts = [`${executed.lots.length} lot(s) quarantined`];
      if (executed.rejected.length) parts.push(`${executed.rejected.length} traveler(s) rejected`);
      if (executed.flagged.length) parts.push(`${executed.flagged.length} flagged`);
      if (executed.shipped.length) parts.push(`${executed.shipped.length} shipped — follow up`);
      toast.success(parts.join(' · '));
      // The action touches lots, travelers and the graph broadly —
      // invalidate the whole lot domain plus the traveler lookups
      // (rejected/flagged travelers got note/status writes) rather than
      // guessing keys.
      [
        'lot', 'lotTrace', 'getLotsData', 'getLotsByItem', 'lotsByIds',
        'lotsUsingSource', 'travelersUsingLot', 'getTravelerData',
        'getTravelersData',
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      onSuccess();
      onClose();
    } catch (submitException) {
      // Non-Error throws have no .message — never show a blank alert.
      setSubmitError(submitException.message || String(submitException));
      setIsSubmitting(false);
    }
  };

  const renderTravelerLine = ({ traveler, affectedCodes }) => (
    <Box key={traveler.id} fontSize="body-s">
      {`Traveler ${traveler.id} — lot(s) ${affectedCodes.join(', ')}`}
      {traveler.order ? ` · Order ${traveler.order}` : ''}
    </Box>
  );

  return (
    <Modal
      visible
      size="large"
      onDismiss={onClose}
      header={`Quarantine — ${lot.code || lot.id}`}
      footer={(
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <span className="lot-danger-button">
              <Button
                onClick={handleSubmit}
                loading={isSubmitting}
                // No plan (trace still loading, or failed) also disables:
                // confirming an action whose preview never rendered would
                // be a blind quarantine.
                disabled={isLoading || !plan || nothingToDo}
              >
                Quarantine
              </Button>
            </span>
          </SpaceBetween>
        </Box>
      )}
    >
      <SpaceBetween direction="vertical" size="m">
        {submitError ? <Alert type="error">{submitError}</Alert> : null}
        {error ? <Alert type="error">{error.message}</Alert> : null}
        {isLoading && <Spinner />}

        {plan && nothingToDo && (
          <Alert type="info">
            Every lot in this graph is already quarantined — there is nothing
            to change.
          </Alert>
        )}

        {plan && !nothingToDo && !hasDownstream && (
          <Box>
            Nothing has been built from this bag — only this lot will be
            quarantined. Quarantined lots are rejected when scanned for
            assembly or install.
          </Box>
        )}

        {plan && !nothingToDo && hasDownstream && (
          <>
            <FormField label="Scope" stretch>
              <RadioGroup
                value={scope}
                onChange={({ detail }) => setScope(detail.value)}
                items={[
                  {
                    value: 'downstream',
                    label: (
                      <Box color="text-status-error">
                        This lot + everything downstream
                      </Box>
                    ),
                    description: `${plan.lots.length} lot(s) quarantined · ${plan.rejected.length} completed traveler(s) rejected · ${plan.flagged.length} in-progress flagged · ${plan.shipped.length} shipped`,
                  },
                  {
                    value: 'single',
                    label: 'This lot only',
                    description: 'The damage happened after earlier consumption — parts already built from this bag are unaffected.',
                  },
                ]}
              />
            </FormField>

            {scope === 'downstream' && (
              <SpaceBetween size="s">
                <div>
                  <Box variant="awsui-key-label">{`Lots to quarantine (${plan.lots.length})`}</Box>
                  <Box fontSize="body-s">
                    {plan.lots.map((planLot) => planLot.code || planLot.id).join(', ')}
                  </Box>
                </div>
                {plan.rejected.length > 0 && (
                  <div>
                    <Box variant="awsui-key-label">
                      {`Completed travelers — last step set to ${InspectionValues.REJECT_MAJOR} (${plan.rejected.length})`}
                    </Box>
                    {plan.rejected.map(renderTravelerLine)}
                  </div>
                )}
                {plan.flagged.length > 0 && (
                  <div>
                    <Box variant="awsui-key-label">{`In-progress travelers — flagged on the affected step (${plan.flagged.length})`}</Box>
                    {plan.flagged.map(renderTravelerLine)}
                  </div>
                )}
                {plan.shipped.length > 0 && (
                  <Alert type="error" header={`${plan.shipped.length} shipped traveler(s) — flagged, but already at the customer. Follow up needed.`}>
                    {plan.shipped.map(renderTravelerLine)}
                  </Alert>
                )}
              </SpaceBetween>
            )}
          </>
        )}

        {!nothingToDo && (
          <FormField label="Reason" errorText={reasonError} stretch>
            <Input
              value={reason}
              onChange={({ detail }) => {
                setReason(detail.value);
                // Typing is the fix — don't keep showing the stale error.
                if (detail.value.trim()) setReasonError('');
              }}
              placeholder="What's wrong with the parts?"
            />
          </FormField>
        )}
      </SpaceBetween>
    </Modal>
  );
}

QuarantineLotModal.propTypes = {
  lot: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
